import React, { useState, useEffect, useContext, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { AuthContext } from '../../../context/AuthContext';
import { 
  getSafetyProcessMasters, 
  saveSafetyProcessMaster, 
  deleteSafetyProcessMaster 
} from '../../../api/safetyAssessApi';
import '../Safety.css';

function SafetyProcess({ comCode = '' }) {
  const { token } = useContext(AuthContext);
  
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState({ typeTag: '', name: '' });
  const [showModal, setShowModal] = useState(false);

  // 인라인 편집
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ typeTag: '', name: '' });

  const fetchProcesses = useCallback(async () => {
    if (!token || !comCode) return;
    try {
      const data = await getSafetyProcessMasters(token, comCode);
      setProcesses(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, comCode]);

  useEffect(() => { fetchProcesses(); }, [fetchProcesses]);

  const handleSave = async (item) => {
    if (!item.name) return alert('공정명을 입력하세요.');
    try {
      await saveSafetyProcessMaster(item, token, comCode);
      await fetchProcesses();
      setNewItem({ typeTag: '', name: '' });
      setShowModal(false);
    } catch (err) {
      alert('저장 실패');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('선택한 공정을 정말 삭제하시겠습니까?')) return;
    try {
      await deleteSafetyProcessMaster(id, token, comCode);
      await fetchProcesses();
    } catch (err) {
      alert('삭제 실패');
    }
  };

  const handleStartEdit = (proc) => {
    setEditingId(proc.id);
    setEditData({ typeTag: proc.typeTag || '', name: proc.name });
  };

  const handleConfirmEdit = async () => {
    if (!editData.name.trim()) return;
    try {
      await saveSafetyProcessMaster({ id: editingId, ...editData }, token, comCode);
      setEditingId(null);
      await fetchProcesses();
    } catch (err) {
      alert('수정 실패');
    }
  };

  // 태그별 그룹핑 (현재 필터)
  const [filterTag, setFilterTag] = useState('all');
  const allTags = [...new Set(processes.map(p => p.typeTag || '일반공정').filter(Boolean))];
  const filteredProcesses = filterTag === 'all'
    ? processes
    : processes.filter(p => (p.typeTag || '일반공정') === filterTag);

  if (loading) {
    return (
      <div className="sf-page">
        <div className="sf-loading">
          <div className="sf-spinner"></div>
          <span className="sf-loading-text">공정 마스터를 불러오는 중입니다...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ animation: 'fadeIn 0.2s ease-in-out' }}>

      <div className="row">
        <div className="col-12">
          {/* 태그 필터 바 */}
          {allTags.length > 1 && (
            <div className="sf-toggle-bar" style={{marginBottom: 20}}>
              <span className="sf-toggle-label">구분 필터:</span>
              <div className="d-flex gap-2 flex-wrap">
                <button
                  className={`sf-btn sf-btn-sm ${filterTag === 'all' ? 'sf-btn-primary' : 'sf-btn-outline'}`}
                  onClick={() => setFilterTag('all')}
                >
                  전체 ({processes.length})
                </button>
                {allTags.map(tag => (
                  <button
                    key={tag}
                    className={`sf-btn sf-btn-sm ${filterTag === tag ? 'sf-btn-primary' : 'sf-btn-outline'}`}
                    onClick={() => setFilterTag(tag)}
                  >
                    {tag} ({processes.filter(p => (p.typeTag || '일반공정') === tag).length})
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 공정 목록 카드 */}
          <div className="sf-card">
            <div className="sf-card-header sf-gradient-dark" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <h5 className="sf-card-header-title" style={{ margin: 0 }}>
                  <i className="bi bi-building-gear me-2"></i>
                  사업장 공정 목록
                </h5>
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <span className="sf-badge sf-badge-white">
                  {filterTag === 'all' ? `총 ${processes.length}개` : `${filteredProcesses.length}개 표시 중`}
                </span>
                <button
                  className="sf-btn sf-btn-sm"
                  style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 700, padding: '0 12px', height: '32px' }}
                  onClick={() => setShowModal(true)}
                >
                  <i className="bi bi-plus-lg me-1"></i> 새 공정 추가
                </button>
              </div>
            </div>

            <div className="sf-card-body">
              {/* 테이블 */}
              {filteredProcesses.length === 0 ? (
                <div className="sf-empty">
                  <div className="sf-empty-icon">🏭</div>
                  <div className="sf-empty-text">
                    {filterTag === 'all' ? '등록된 공정이 없습니다. 우측 상단의 추가 버튼을 눌러주세요.' : `"${filterTag}" 태그에 해당하는 공정이 없습니다.`}
                  </div>
                </div>
              ) : (
                <div className="table-responsive" style={{marginBottom: 24}}>
                  <table className="sf-table">
                    <thead>
                      <tr>
                        <th style={{width: '60px', textAlign: 'center'}}>No</th>
                        <th style={{width: '250px'}}>공정 구분 태그</th>
                        <th>공정명</th>
                        <th style={{width: '180px', textAlign: 'center'}}>관리</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProcesses.map((proc, idx) => {
                        const isEditing = editingId === proc.id;
                        return (
                          <tr key={proc.id} style={{verticalAlign: 'middle'}}>
                            <td style={{textAlign: 'center', fontWeight: 600, color: 'var(--sf-text-muted)'}}>{idx + 1}</td>
                            <td>
                              {isEditing ? (
                                <input
                                  className="sf-input"
                                  type="text"
                                  value={editData.typeTag}
                                  onChange={e => setEditData({ ...editData, typeTag: e.target.value })}
                                  placeholder="예: 청소, 운반"
                                  style={{padding: '6px 10px', fontSize: '0.88rem'}}
                                />
                              ) : (
                                <span className="sf-tag sf-tag-accent">
                                  {proc.typeTag || '일반공정'}
                                </span>
                              )}
                            </td>
                            <td style={{maxWidth: 0}}>
                              {isEditing ? (
                                <div className="d-flex align-items-center gap-2">
                                  <input
                                    className="sf-input"
                                    type="text"
                                    value={editData.name}
                                    onChange={e => setEditData({ ...editData, name: e.target.value })}
                                    onKeyDown={e => e.key === 'Enter' && handleConfirmEdit()}
                                    autoFocus
                                    style={{padding: '6px 10px', fontSize: '0.88rem', flex: 1}}
                                  />
                                  <button className="sf-btn sf-btn-primary sf-btn-sm" onClick={handleConfirmEdit} style={{borderRadius: 6, height: 28, padding: '0 12px', fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap'}}>
                                    저장
                                  </button>
                                  <button className="sf-btn sf-btn-sm" onClick={() => setEditingId(null)} style={{borderRadius: 6, height: 28, padding: '0 12px', fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1'}}>
                                    취소
                                  </button>
                                </div>
                              ) : (
                                <div title={proc.name} onDoubleClick={() => handleStartEdit(proc)} style={{fontWeight: 600, color: 'var(--sf-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', cursor: 'pointer'}}>{proc.name}</div>
                              )}
                            </td>
                            <td style={{textAlign: 'center'}}>
                              {!isEditing && (
                                <div className="d-flex align-items-center justify-content-center gap-2">
                                  <button className="sf-btn sf-btn-sm" onClick={() => handleStartEdit(proc)} style={{ borderRadius: 6, height: 28, display: 'inline-flex', alignItems: 'center', fontWeight: 700, background: '#eff6ff', color: '#2563eb', border: 'none', padding: '0 12px', fontSize: '0.8rem' }}>
                                    수정
                                  </button>
                                  <button className="sf-btn sf-btn-sm" onClick={() => handleDelete(proc.id)} style={{ borderRadius: 6, height: 28, display: 'inline-flex', alignItems: 'center', fontWeight: 700, background: '#fef2f2', color: '#ef4444', border: 'none', padding: '0 12px', fontSize: '0.8rem' }}>
                                    삭제
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* 컴팩트형 B2B 표준 모달 팝업 오버레이 */}
      {showModal && ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(2px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'fadeIn 0.2s' }}>
          <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', width: '460px', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', animation: 'slideUp 0.2s ease-out' }}>
            
            {/* 모달 상단 헤더 (연한 회색 배경) */}
            <div style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i className="bi bi-layers" style={{ color: '#3b82f6', fontSize: '1.2rem' }}></i>
                새 표준 공정 추가
              </div>
              <button onClick={() => setShowModal(false)} style={{ background: 'transparent', border: 'none', fontSize: '1.2rem', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            
            {/* 모달 본문 (흰색 배경) */}
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569', marginBottom: '8px', display: 'block' }}>
                  <span style={{ color: '#3b82f6', marginRight: '6px', fontWeight: 800 }}>H</span>구분 태그 <span style={{ color: '#94a3b8', fontWeight: 500, fontSize: '0.75rem' }}>(선택)</span>
                </label>
                <input
                  type="text"
                  placeholder="예: 청소, 운반, 이동"
                  value={newItem.typeTag}
                  onChange={(e) => setNewItem({...newItem, typeTag: e.target.value})}
                  style={{ width: '100%', backgroundColor: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', height: '40px', padding: '0 12px', fontSize: '0.9rem', color: '#0f172a', outline: 'none', transition: 'all 0.2s' }}
                  onFocus={e => {e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 2px rgba(59,130,246,0.1)';}}
                  onBlur={e => {e.target.style.borderColor = '#cbd5e1'; e.target.style.boxShadow = 'none';}}
                  autoFocus
                />
              </div>
              
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569', marginBottom: '8px', display: 'block' }}>
                  <span style={{ color: '#3b82f6', marginRight: '6px', fontWeight: 800 }}>H</span>공정명 <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="예: 객실 내부 청소 및 세팅"
                  value={newItem.name}
                  onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                  onKeyDown={e => e.key === 'Enter' && handleSave(newItem)}
                  style={{ width: '100%', backgroundColor: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', height: '40px', padding: '0 12px', fontSize: '0.9rem', color: '#0f172a', outline: 'none', transition: 'all 0.2s' }}
                  onFocus={e => {e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 2px rgba(59,130,246,0.1)';}}
                  onBlur={e => {e.target.style.borderColor = '#cbd5e1'; e.target.style.boxShadow = 'none';}}
                />
              </div>
            </div>

            {/* 모달 하단 풋터 (연한 회색 배경) */}
            <div style={{ backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', padding: '16px 24px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setShowModal(false)} 
                style={{ borderRadius: '6px', padding: '0 16px', height: '36px', fontSize: '0.85rem', fontWeight: 600, color: '#475569', background: '#ffffff', border: '1px solid #cbd5e1', cursor: 'pointer' }}
              >
                취소
              </button>
              <button 
                onClick={() => handleSave(newItem)} 
                style={{ borderRadius: '6px', padding: '0 20px', height: '36px', fontSize: '0.85rem', fontWeight: 700, color: '#ffffff', background: '#3b82f6', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <i className="bi bi-check2"></i> 공정 추가
              </button>
            </div>

          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default SafetyProcess;
